// src/pages/Admin/Business/Finance/modals/ExpenseModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaBuilding,
  FaCalendarAlt,
  FaEuroSign,
  FaFileInvoice,
  FaPlusCircle,
  FaReceipt,
  FaSave,
  FaTag,
  FaTimes,
  FaUserTie,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../../../../../../components/Button/Button";
import { getAllAssociations } from "../../../../../../services/associationsServices";
import {
  addDepensePro,
  updateDepensePro,
} from "../../../../../../services/depensesproServices";
import {
  fmtMoney,
  toInputDate,
} from "../financeCommitments.utils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-indigo-900/40";

function buildInitialForm(expense) {
  return {
    asso_id: expense?.asso_id || "",
    nom_depense: expense?.nom_depense || "",
    destinataire_depense: expense?.destinataire_depense || "",
    date_depense: toInputDate(expense?.date_depense) || toInputDate(new Date()),
    montant_depense: expense?.montant_depense ?? "",
    categorie_depense: expense?.categorie_depense || "",
    facture_depense: expense?.facture_depense || "",
  };
}

const ExpenseModal = ({ isOpen, onClose, expense, refreshList }) => {
  const isEdit = Boolean(expense?.id);

  const [formData, setFormData] = useState(buildInitialForm(expense));
  const [associations, setAssociations] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(buildInitialForm(expense));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, expense, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchAssociations = async () => {
      try {
        setLoadingAssociations(true);
        const data = await getAllAssociations();
        setAssociations(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error("Erreur lors du chargement des associations.");
      } finally {
        setLoadingAssociations(false);
      }
    };

    fetchAssociations();
  }, [isOpen]);

  const selectedAssociationName = useMemo(() => {
    const found = associations.find(
      (asso) => String(asso.id) === String(formData.asso_id)
    );
    return found?.nom_asso || "Aucune association sélectionnée";
  }, [associations, formData.asso_id]);

  const amountPreview = useMemo(() => {
    const value = Number(formData.montant_depense);
    if (!Number.isFinite(value) || value <= 0) return "—";
    return fmtMoney(value, true);
  }, [formData.montant_depense]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.asso_id ||
      !formData.nom_depense ||
      !formData.destinataire_depense ||
      !formData.date_depense ||
      !formData.montant_depense ||
      !formData.categorie_depense
    ) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }

    try {
      setSaving(true);

      if (isEdit) {
        await updateDepensePro(expense.id, formData);
        toast.success("Dépense modifiée avec succès.");
      } else {
        await addDepensePro(formData);
        toast.success("Dépense ajoutée avec succès.");
      }

      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement de la dépense.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.45)] dark:bg-slate-900">
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.26),transparent_28%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.22),transparent_24%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />
            <div className="relative px-5 py-5 sm:px-6 sm:py-6 text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                    <FaPlusCircle />
                    {isEdit ? "Modification" : "Nouvelle dépense"}
                  </div>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                    {isEdit ? "Modifier une dépense" : "Ajouter une dépense"}
                  </h3>

                  <p className="mt-1 max-w-2xl text-sm text-white/75">
                    Saisis la dépense réelle avec son montant, sa date et son
                    destinataire.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                <div className="xl:col-span-8 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Association & dépense"
                      subtitle="L’élément concerné et son bénéficiaire."
                    />

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field>
                        <Label required>Association</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaBuilding />
                          </span>
                          <select
                            className={`${inputClass} pl-10`}
                            name="asso_id"
                            value={formData.asso_id}
                            onChange={handleChange}
                            required
                          >
                            <option value="">
                              {loadingAssociations ? "Chargement..." : "Sélectionnez une association"}
                            </option>
                            {associations.map((asso) => (
                              <option key={asso.id} value={asso.id}>
                                {asso.nom_asso}
                              </option>
                            ))}
                          </select>
                        </div>
                      </Field>

                      <Field>
                        <Label required>Nom de la dépense</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaReceipt />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="nom_depense"
                            value={formData.nom_depense}
                            onChange={handleChange}
                            placeholder="Ex : Hébergement VPS"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Destinataire</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaUserTie />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="destinataire_depense"
                            value={formData.destinataire_depense}
                            onChange={handleChange}
                            placeholder="Ex : Hostinger"
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Catégorie</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaTag />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="categorie_depense"
                            value={formData.categorie_depense}
                            onChange={handleChange}
                            placeholder="Ex : Hébergement"
                            required
                          />
                        </div>
                      </Field>
                    </div>
                  </section>

                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Informations financières"
                      subtitle="Date, montant et facture."
                    />

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field>
                        <Label required>Date de dépense</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaCalendarAlt />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="date"
                            name="date_depense"
                            value={formData.date_depense}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Montant (€)</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaEuroSign />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="number"
                            step="0.01"
                            min="0"
                            name="montant_depense"
                            value={formData.montant_depense}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </Field>

                      <Field className="md:col-span-2">
                        <Label>Facture / lien / référence</Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaFileInvoice />
                          </span>
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="facture_depense"
                            value={formData.facture_depense}
                            onChange={handleChange}
                            placeholder="Lien Drive, nom de fichier, numéro de facture..."
                          />
                        </div>
                      </Field>
                    </div>
                  </section>
                </div>

                <div className="xl:col-span-4 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Résumé"
                      subtitle="Aperçu avant validation."
                    />

                    <div className="mt-5 space-y-4">
                      <SummaryRow
                        icon={<FaBuilding />}
                        label="Association"
                        value={selectedAssociationName}
                      />
                      <SummaryRow
                        icon={<FaReceipt />}
                        label="Libellé"
                        value={formData.nom_depense || "Non renseigné"}
                      />
                      <SummaryRow
                        icon={<FaUserTie />}
                        label="Destinataire"
                        value={formData.destinataire_depense || "Non renseigné"}
                      />
                      <SummaryRow
                        icon={<FaEuroSign />}
                        label="Montant"
                        value={amountPreview}
                      />
                      <SummaryRow
                        icon={<FaCalendarAlt />}
                        label="Date"
                        value={formData.date_depense || "—"}
                      />
                    </div>
                  </section>
                </div>
              </div>
            </form>
          </div>

          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={onClose}
                className="gap-2 !rounded-2xl"
              />
              <Button
                text={saving ? "Enregistrement..." : isEdit ? "Enregistrer" : "Ajouter la dépense"}
                icon={FaSave}
                type="submit"
                onClick={handleSubmit}
                disabled={saving}
                className="gap-2 !rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h4>
      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function Field({ children }) {
  return <div>{children}</div>;
}

function Label({ children, required = false }) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          {label}
        </div>
        <div className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export default ExpenseModal;